<template>
<div>
  <data-table
    id-column="id"
    sortable
    :canDeleteRow="canDeleteRow"
    hasDeleteAction
    hasPreviewAction
    @deleteRow="deleteRowHandler"
    @previewRow="previewRowHandler"
    :sortingOptions="sortingOptions"
    :columns="columns"
    :rows="list"></data-table>

   <transition name="fade">
      <Modal
        v-if="showDeleteModal"
        @close="hideModal"
        @destructiveButtonClick="doDeletePreview"
        :destructiveButtonText="$t('action.delete')"
        :title="$t('campaignDesigner.targetDefinition.deletePreview')">
        <div class="slds-text-heading_small slds-text-align_center">
          {{$t('campaignDesigner.targetDefinition.deletePreviewConfirm')}}
        </div>
      </Modal>
    </transition>

    <transition name="fade">
      <Modal
        size="large"
        v-if="showPreviewModal"
        @close="hidePreviewModal"
        @destructiveButtonClick="hidePreviewModal"
        :destructiveButtonText="$t('general.close')"
        :title="$t('fastCounter.previewList.showPreview')">
        <spinner v-if="previewLoading" />
        <template v-else>
          <div class="container slds-text-align_center" v-if="previewStatus === 'COMPUTING'">
            <span class="slds-text-heading_small ">
              {{ $t('campaignDesigner.finalCheckAndCampaignRelease.computingPreview') }}
            </span>
          </div>
          <div v-else-if="previewStatus === 'ERROR'">
            <section class="slds-modal_prompt slds-fade-in-open">
              <div class="slds-modal__container">
                <header class="slds-modal__header slds-theme_error slds-theme_alert-texture">
                  <h2 class="slds-text-heading_small" id="prompt-heading-id">{{$t('fastCounter.previewList.previewError')}}</h2>
                </header>
                <div class="slds-modal__content slds-p-around_medium slds-text-align_center" id="prompt-message-wrapper">
                  <p>{{ $t('fastCounter.previewList.previewError') }}</p>
                </div>
              </div>
            </section>
          </div>
          <div v-else-if="previewStatus === 'COMPUTED'">
            <template v-if="preview.previewData && Object.keys(preview.previewData).length">
              <!-- Table -->
              <table class="slds-table slds-table_cell-buffer slds-table_bordered slds-m-bottom--medium">
                  <thead>
                    <tr class="slds-line-height_reset">
                      <th class="" scope="col">
                        <div class="slds-truncate">{{$t('campaignDesigner.finalCheckAndCampaignRelease.numberOfContacts')}}</div>
                      </th>
                      <th class="" scope="col">
                        <div class="slds-truncate">{{$t('campaignDesigner.finalCheckAndCampaignRelease.numberOfCars')}}</div>
                      </th>
                      <th class="" scope="col">
                        <div class="slds-truncate">{{$t('campaignDesigner.finalCheckAndCampaignRelease.numberOfContactsEmailGranted')}}</div>
                      </th>
                      <th class="" scope="col">
                        <div class="slds-truncate">{{$t('campaignDesigner.finalCheckAndCampaignRelease.numberOfContactsSmsGranted')}}</div>
                      </th>
                      <th class="" scope="col">
                        <div class="slds-truncate">{{$t('campaignDesigner.finalCheckAndCampaignRelease.numberOfDistinctEmail')}}</div>
                      </th>
                      <th class="" scope="col">
                        <div class="slds-truncate">{{$t('campaignDesigner.finalCheckAndCampaignRelease.numberOfDistinctSMS')}}</div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <div class="slds-truncate">{{preview.previewData.totalCount.contact}}</div>
                      </td>
                      <td>
                        <div class="slds-truncate">{{preview.previewData.totalCount.car}}</div>
                      </td>
                      <td>
                        <div class="slds-truncate">{{preview.previewData.totalCount.contactEmailGranted}}</div>
                      </td>
                      <td>
                        <div class="slds-truncate">{{preview.previewData.totalCount.contactSmsGranted}}</div>
                      </td>
                      <td>
                        <div class="slds-truncate">
                          {{preview.previewData.totalCount.email}}
                        </div>
                      </td>
                      <td>
                        <div class="slds-truncate">
                          {{preview.previewData.totalCount.sms}}
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
            </template>
            <template v-else>
              <div class="container slds-text-align_center">
                <span class="slds-text-heading_small slds-text-align_center">
                  {{ $t('fastCounter.previewList.previewNotAvailable') }}
                </span>
              </div>
            </template>
          </div>
      </template>
      </Modal>
    </transition>
</div>

</template>

<script>

import { mapActions } from 'vuex'
import DataTable from '../../components/slds/DataTable2'
import CheckPermission from '../utils/CheckPermission'
import Spinner from '../../components/Spinner'
import Modal from '../Modal'
import { getErrorMessage } from '../utils/utils'

export default {
  mixins: [CheckPermission],
  components: {
    DataTable,
    Modal,
    Spinner
  },
  props: {
    list: {
      type: Array,
      required: true,
      default: () => []
    }
  },
  computed: {
    previewStatus () {
      if (this.preview.previewData && Object.keys(this.preview.previewData).length >= 0) {
        return 'COMPUTED'
      }
      if (this.previewError) {
        return 'ERROR'
      }
      if (!this.preview) {
        return 'COMPUTING'
      }
      return 'MISSING'
    }
  },
  methods: {
    canDeleteRow(row){
      return this.$store.getters['session/userId'] === row.createdById || this.checkPermission('DELETE_FAST_COUNTER')
    },
    deleteRowHandler(event) {
      this.previewIdToDelete = event.row.id
      this.showDeleteModal = true
    },
    async previewRowHandler(event){
      this.previewLoading = true
      this.showPreviewModal = true
      this.previewError = false

      const preview = await this.getPreview(event.row.id)
      this.previewLoading = false

      if (preview.httpCode && preview.httpCode === '404') {
          this.previewError = true
          this.showToast('error', this.$t('general.error'), 'text', 'Preview error')
          return
        } else {
          this.preview.previewData = preview.preview ? preview.preview.preview.pop().preview : {}
        }
    },
    hideModal () {
      this.previewIdToDelete = -1
      this.showDeleteModal = false
    },
    hidePreviewModal () {
      this.showPreviewModal = false
    },
    async doDeletePreview () {
      try {
        await this.deletePreview({previewId : this.previewIdToDelete})
        this.showToast('success', this.$t('message.delete'), 'text')
        this.$emit('update')
        this.showDeleteModal = false
      } catch (error) {
        console.error(error)
        this.showToast('error', this.$t('general.error'), 'text', getErrorMessage(error))
      }
    },
    ...mapActions(
      {
        deletePreview: 'fastCounter/deletePreview',
        getPreview : 'fastCounter/getPreview'
      }
    )
  },
  data () {
    return {
      columns: [
        {
          label: 'Id',
          type: 'url',
          options: {
            linkPrefix: 'preview',
            targetField: 'id'
          }
        },
        {
          label: 'Name',
          type: 'url',
          options: {
            linkPrefix: 'preview',
            targetField: 'id'
          }
        },
        {
          label: 'Created Date',
          type: 'date',
        },
        {
          label: 'Last Modified Date',
          type: 'date',
        },
        {
          label: 'Created By',
          type: 'text',
        }
      ],
      preview: {
        previewData: {
        }
      },
      previewError: false,
      previewLoading:false,
      showDeleteModal: false,
      showPreviewModal: false
    }
  },
  created(){
    this.sortingOptions = {
        field: 2,
        direction: "desc"
      }
  }
}
</script>
